/**
 * 🚀 CONFIGURACIÓN DE RENDIMIENTO PARA ANGULAR
 * =============================================
 * 
 * Este archivo contiene configuraciones y optimizaciones para mejorar
 * el rendimiento de la aplicación Angular eliminando las violaciones de setTimeout
 */

// 1. CONFIGURACIÓN DE ZONE.JS PARA REDUCIR OVERHEAD
(window as any).__Zone_disable_requestAnimationFrame = true; // Desactivar RAF en zone.js si no es necesario
(window as any).__Zone_disable_on_property = true; // Desactivar monkey patching de propiedades
(window as any).__Zone_disable_geolocation = true; // Desactivar geolocation si no se usa
(window as any).__Zone_disable_file = true; // Desactivar file API si no se usa
(window as any).__Zone_disable_FileReader = true; // Desactivar FileReader si no se usa

// 2. CONFIGURACIÓN DE TIMEOUTS GLOBALES
class PerformanceOptimizer {
  private static timeoutCount = 0;
  private static intervalCount = 0;
  private static readonly MAX_TIMEOUTS = 15; // 🚀 Reducido de 20 a 15 para mayor control
  private static readonly MAX_INTERVALS = 8; // 🚀 Reducido de 10 a 8 para mayor control

  /**
   * Wrapper optimizado para setTimeout que incluye monitoring
   */
  static safeTimeout(callback: () => void, delay: number): any {
    if (this.timeoutCount >= this.MAX_TIMEOUTS) {
      console.warn(`⚠️ [PERFORMANCE] Demasiados timeouts activos: ${this.timeoutCount}`);
      // 🚀 OPTIMIZACIÓN: Usar requestAnimationFrame si hay demasiados timeouts
      requestAnimationFrame(callback);
      return -1;
    }

    this.timeoutCount++;
    const timeoutId = setTimeout(() => {
      this.timeoutCount = Math.max(0, this.timeoutCount - 1);
      callback();
    }, delay);

    return timeoutId;
  }

  /**
   * Wrapper optimizado para setInterval
   */
  static safeInterval(callback: () => void, delay: number): any {
    if (this.intervalCount >= this.MAX_INTERVALS) {
      console.warn(`⚠️ [PERFORMANCE] Demasiados intervals activos: ${this.intervalCount}`);
      return -1;
    }

    this.intervalCount++;
    const intervalId = setInterval(() => {
      callback();
    }, delay);

    return intervalId;
  }

  /**
   * Limpiar timeout con tracking
   */
  static clearTimeout(timeoutId: any): void {
    if (timeoutId !== -1) {
      this.timeoutCount = Math.max(0, this.timeoutCount - 1);
      clearTimeout(timeoutId);
    }
  }

  /**
   * Limpiar interval con tracking
   */
  static clearInterval(intervalId: any): void {
    if (intervalId !== -1) {
      this.intervalCount = Math.max(0, this.intervalCount - 1);
      clearInterval(intervalId);
    }
  }

  /**
   * Obtener estadísticas de rendimiento
   */
  static getStats() {
    return {
      timeouts: this.timeoutCount,
      intervals: this.intervalCount,
      timestamp: Date.now()
    };
  }

  /**
   * Monitor de performance que se ejecuta cada 15 segundos
   */
  static startPerformanceMonitor() {
    // Permitir pausar completamente con flag runtime (?safe=1)
    try {
      if (localStorage.getItem('PERF_GUARD_PAUSE') === '1') {
        return;
      }
    } catch {}
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const monitorDelay = isSafari ? 30000 : 15000; // Safari: menos frecuencia
    // Guardar id para poder pausar/reanudar según visibilidad
    const run = () => {
      const stats = this.getStats();
      if (stats.timeouts > 8 || stats.intervals > 4) {
        console.warn('⚠️ [PERFORMANCE MONITOR]', stats);
      }
    };

    // Crear intervalo controlado
    const id = setInterval(run, monitorDelay);
    (this as any)._monitorId = id;

    const onVisibility = () => {
      try {
        const hidden = document.hidden;
        const currentId = (this as any)._monitorId;
        if (hidden && currentId) {
          clearInterval(currentId);
          (this as any)._monitorId = null;
        } else if (!hidden && !(this as any)._monitorId) {
          (this as any)._monitorId = setInterval(run, monitorDelay);
        }
      } catch {}
    };

    document.addEventListener('visibilitychange', onVisibility);
    (this as any)._onVisibilityHandler = onVisibility;
  }

  static stopPerformanceMonitor() {
    const currentId = (this as any)._monitorId;
    if (currentId) {
      clearInterval(currentId);
      (this as any)._monitorId = null;
    }
    const handler = (this as any)._onVisibilityHandler;
    if (handler) {
      document.removeEventListener('visibilitychange', handler);
      (this as any)._onVisibilityHandler = null;
    }
  }
}

// 3. CONFIGURACIÓN DE DETECCIÓN DE FULLSCREEN OPTIMIZADA
class FullscreenOptimizer {
  private static isMonitoring = false;
  private static callbacks: (() => void)[] = [];
  private static checkInterval: any = null;

  /**
   * Registrar callback para cambios de fullscreen
   */
  static addListener(callback: () => void): void {
    this.callbacks.push(callback);
    
    if (!this.isMonitoring) {
      this.startMonitoring();
    }
  }

  /**
   * Remover callback
   */
  static removeListener(callback: () => void): void {
    const index = this.callbacks.indexOf(callback);
    if (index > -1) {
      this.callbacks.splice(index, 1);
    }

    if (this.callbacks.length === 0) {
      this.stopMonitoring();
    }
  }

  /**
   * Iniciar monitoring optimizado (eventos + polling reducido)
   */
  private static startMonitoring(): void {
    try {
      if (localStorage.getItem('PERF_GUARD_PAUSE') === '1') {
        return; // no iniciar en modo seguro
      }
    } catch {}
    this.isMonitoring = true;

    // Eventos nativos (más eficientes)
    const events = [
      'fullscreenchange',
      'webkitfullscreenchange',
      'mozfullscreenchange',
      'MSFullscreenChange'
    ];

    events.forEach(event => {
      document.addEventListener(event, () => {
        this.notifyCallbacks();
      });
    });

    // Polling como respaldo (cada 2 segundos; deshabilitable por modo seguro)
    try {
      if (localStorage.getItem('PERF_GUARD_PAUSE') !== '1') {
        this.checkInterval = setInterval(() => {
          this.notifyCallbacks();
        }, 2000);
      }
    } catch {
      this.checkInterval = setInterval(() => {
        this.notifyCallbacks();
      }, 2000);
    }
  }

  /**
   * Detener monitoring
   */
  private static stopMonitoring(): void {
    this.isMonitoring = false;
    
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Notificar a todos los callbacks
   */
  private static notifyCallbacks(): void {
    this.callbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error en callback de fullscreen:', error);
      }
    });
  }
}

// 4. DEBOUNCE GLOBAL OPTIMIZADO
class DebounceOptimizer {
  private static timeouts = new Map<string, any>();

  /**
   * Debounce optimizado con cleanup automático
   */
  static debounce(key: string, callback: () => void, delay: number = 300): void {
    // Limpiar timeout anterior si existe
    if (this.timeouts.has(key)) {
      clearTimeout(this.timeouts.get(key));
    }

    // Crear nuevo timeout
    const timeoutId = setTimeout(() => {
      callback();
      this.timeouts.delete(key);
    }, delay);

    this.timeouts.set(key, timeoutId);
  }

  /**
   * Limpiar todos los debounces
   */
  static clearAll(): void {
    this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.timeouts.clear();
  }
}

// 5. LAZY LOADING OPTIMIZER
class LazyLoadOptimizer {
  /**
   * Procesar array grande en chunks para no bloquear UI
   */
  static async processArrayInChunks<T>(
    array: T[], 
    processor: (item: T) => any, 
    chunkSize: number = 10
  ): Promise<any[]> {
    const results: any[] = [];
    
    for (let i = 0; i < array.length; i += chunkSize) {
      const chunk = array.slice(i, i + chunkSize);
      
      // Procesar chunk
      const chunkResults = chunk.map(processor);
      results.push(...chunkResults);
      
      // Yield control al browser
      await new Promise(resolve => requestAnimationFrame(() => resolve(null)));
    }
    
    return results;
  }

  /**
   * Ejecutar función pesada con yield control
   */
  static async executeWithYield(callback: () => void): Promise<void> {
    return new Promise(resolve => {
      requestAnimationFrame(() => {
        callback();
        resolve();
      });
    });
  }
}

// 6. EXPORTAR UTILIDADES GLOBALES
(window as any).PerformanceOptimizer = PerformanceOptimizer;
(window as any).FullscreenOptimizer = FullscreenOptimizer;
(window as any).DebounceOptimizer = DebounceOptimizer;
(window as any).LazyLoadOptimizer = LazyLoadOptimizer;

// 7. INICIALIZAR MONITORING
PerformanceOptimizer.startPerformanceMonitor();

// console.log('🚀 [PERFORMANCE] Optimizadores de rendimiento cargados');
// console.log('🔧 [DEBUG] Disponibles en window: PerformanceOptimizer, FullscreenOptimizer, DebounceOptimizer, LazyLoadOptimizer');

/**
 * 🎯 CÓMO USAR EN LOS COMPONENTES:
 * 
 * // En lugar de setTimeout():
 * PerformanceOptimizer.safeTimeout(() => { ... }, 1000);
 * 
 * // Para fullscreen:
 * FullscreenOptimizer.addListener(() => { ... });
 * 
 * // Para debounce:
 * DebounceOptimizer.debounce('validation', () => { ... }, 300);
 * 
 * // Para procesar arrays grandes:
 * const results = await LazyLoadOptimizer.processArrayInChunks(videos, video => enrichVideo(video));
 */

export { PerformanceOptimizer, FullscreenOptimizer, DebounceOptimizer, LazyLoadOptimizer };
